/**
 * Iter-110 — Collaborator detail (Delivery)
 *
 * Shows every mission the collaborator is assigned to. Missions where the current
 * user is NOT the manager are rendered as "name-only" rows for MANAGER role.
 * CEO and GESTIONNAIRE always get the full detail link with expandable info.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Lock, ExternalLink, Briefcase } from "lucide-react";
import { useAuth } from "@/lib/auth";
import MissionNotifications from "@/components/MissionNotifications";

const STATUS_TONE = {
  ACTIVE: "bg-emerald-100 text-emerald-800",
  PENDING_VALIDATION: "bg-amber-100 text-amber-800",
  DRAFT: "bg-zinc-100 text-zinc-700",
  CLOSED: "bg-zinc-200 text-zinc-600",
};

/**
 * Iter-113 — compact envelope bar showing consumed vs remaining JH for the
 * collaborator on the mission. Renders a colored progress bar (green → amber →
 * red as consumption grows) plus a textual "X.X / Y j" caption underneath.
 * Falls back to em-dash when envelope is 0 (mission not budgeted yet).
 */
function EnvelopeBar({ envelope, consumed }) {
  const env = Number(envelope) || 0;
  const used = Number(consumed) || 0;
  if (env <= 0) {
    return (
      <div className="text-[10px] font-mono text-zinc-400" title="Aucune enveloppe budgétée pour ce collaborateur">—</div>
    );
  }
  const pctRaw = env > 0 ? (used / env) * 100 : 0;
  const pct = Math.max(0, Math.min(100, pctRaw));
  const overflow = pctRaw > 100;
  const tone = overflow
    ? "bg-red-600"
    : pct >= 90
      ? "bg-orange-500"
      : pct >= 70
        ? "bg-amber-400"
        : "bg-emerald-500";
  const captionTone = overflow ? "text-red-700" : "text-zinc-700";
  const remaining = Math.max(0, env - used);
  return (
    <div className="min-w-[110px]" title={`${used} / ${env} JH consommés${overflow ? " (dépassement)" : ""}`}>
      <div className="h-2 w-full bg-zinc-100 rounded-full overflow-hidden border border-zinc-200">
        <div className={`h-full ${tone} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <div className={`text-[10px] font-mono mt-1 ${captionTone}`}>
        {Number(used.toFixed(2))} / {Number(env.toFixed(2))} j
        <span className="text-zinc-400"> · reste {Number(remaining.toFixed(2))}</span>
      </div>
    </div>
  );
}

export default function CollaboratorDetail() {
  const { user_id } = useParams();
  const { user: me } = useAuth();
  const [collaborator, setCollaborator] = useState(null);
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [u, ms] = await Promise.all([
          api.get(`/users/${user_id}`).catch(() => ({ data: null })),
          api.get(`/missions?assigned_to=${user_id}`).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setCollaborator(u.data);
        setMissions(ms.data || []);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [user_id]);

  const isAdminScope = me.role === "CEO" || me.role === "GESTIONNAIRE";

  // Iter-110: a manager can only expand missions they manage.
  const canExpand = (m) => isAdminScope || m.manager_id === me.user_id;

  const sorted = useMemo(() => {
    const order = { ACTIVE: 0, PENDING_VALIDATION: 1, DRAFT: 2, CLOSED: 3 };
    return [...missions].sort((a, b) =>
      (order[a.status] ?? 99) - (order[b.status] ?? 99) ||
      (a.title || "").localeCompare(b.title || "")
    );
  }, [missions]);

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1200px]" data-testid="collaborator-detail-page">
      <Link to="/collaborators" data-testid="collab-back" className="inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-brand-navy mb-4">
        <ArrowLeft size={14} /> All collaborators
      </Link>

      {loading && <div className="text-sm text-zinc-500">Loading…</div>}

      {!loading && collaborator && (
        <>
          <div className="text-[11px] uppercase tracking-[0.25em] text-brand-navy-500 mb-3 font-mono">Collaborator</div>
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 rounded-full bg-brand-navy text-white inline-flex items-center justify-center text-xl font-semibold shrink-0">
              {(collaborator.name || collaborator.email || "?").trim().charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold tracking-tight">{collaborator.name || "—"}</h1>
              <div className="text-xs text-zinc-500 mt-1 font-mono">
                {collaborator.role}{collaborator.is_freelance ? " · Freelance" : ""} · {collaborator.email}
              </div>
            </div>
          </div>

          <h2 className="text-sm uppercase tracking-[0.2em] font-mono text-zinc-600 mb-3">
            Missions assigned ({sorted.length})
          </h2>

          {sorted.length === 0 && (
            <div className="bg-white border border-zinc-200 rounded-lg px-5 py-12 text-center text-sm text-zinc-500" data-testid="collab-missions-empty">
              No missions assigned yet.
            </div>
          )}

          <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden divide-y divide-zinc-100">
            {sorted.map((m) => {
              const expandable = canExpand(m);
              const total_jh = (m.roles || []).reduce((acc, r) => acc + (Number(r.budget_jh) || 0), 0);
              const status_tone = STATUS_TONE[m.status] || "bg-zinc-100 text-zinc-700";
              return (
                <div
                  key={m.mission_id}
                  data-testid={`collab-mission-row-${m.mission_id}`}
                  className="px-4 py-3 grid grid-cols-12 gap-3 items-center"
                >
                  <div className="col-span-4 flex items-center gap-2 min-w-0">
                    <Briefcase size={14} className="text-zinc-400 shrink-0" />
                    <div className="truncate">
                      <div className="text-sm font-medium truncate">{m.title}</div>
                      {expandable && m.client_name && <div className="text-[10px] text-zinc-500 font-mono">{m.client_name}</div>}
                    </div>
                  </div>
                  {expandable ? (
                    <>
                      <div className="col-span-1 text-xs">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-mono ${status_tone}`}>{m.status}</span>
                      </div>
                      <div className="col-span-2" data-testid={`collab-mission-envelope-${m.mission_id}`}>
                        <EnvelopeBar envelope={m.envelope_jh} consumed={m.consumed_jh} />
                      </div>
                      <div className="col-span-1 text-xs font-mono text-zinc-700">{m.currency || "—"}</div>
                      <div className="col-span-3" data-testid={`collab-mission-notifs-${m.mission_id}`}>
                        <MissionNotifications mission={m} />
                      </div>
                      <div className="col-span-1 flex justify-end">
                        <Link
                          to={`/missions/${m.mission_id}`}
                          data-testid={`collab-mission-open-${m.mission_id}`}
                          className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline"
                        >
                          Open <ExternalLink size={11} />
                        </Link>
                      </div>
                    </>
                  ) : (
                    <div className="col-span-8 flex items-center justify-end gap-2 text-[11px] text-zinc-500">
                      <Lock size={12} /> Restricted — managed by another lead
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
